// "Monitors" (DESIGN D6): the collapsed registry - one row per monitor on the page (id, trigger, cadence, last write,
// age, legacy, coverage note), the monitors only the store knows, the ones left off the page and why, and the monitors
// the store check excludes. Reference only: every check of every monitor is drawn on the asset it landed on.
import type { HealthMonitor, HealthResponse } from "./types";
import { fmtAgeH, fmtUtc, plural } from "./present";

function triggerCell(m: HealthMonitor): string {
  if (m.trigger === "push") return "push";
  if (m.trigger === "schedule") return m.cron ? `schedule ${m.cron}` : "schedule";
  return "none";
}

function cadenceCell(m: HealthMonitor): string {
  if (typeof m.cadence_h === "number") return `${m.cadence_h}h`;
  return m.trigger === "push" ? "none (push)" : "unknown";
}

export function MonitorsRegistry({ data }: { data: HealthResponse }) {
  const rows = [...data.monitors.map(m => ({ m, unreg: false })), ...data.unregistered_monitors.map(m => ({ m, unreg: true }))];
  const ex = data.coverage.excluded_monitors;
  return (
    <details className="ph-registry" data-testid="monitors-registry">
      <summary>
        <span className="ph-reg-title">MONITORS</span>{" "}
        <span className="ph-hint">
          {plural(data.monitors.length, "registered monitor")}
          {data.unregistered_monitors.length > 0 ? ` · ${data.unregistered_monitors.length} in the store only` : ""} · {ex.length} left off the
          page · expand
        </span>
      </summary>
      <div className="ph-table-wrap">
        <table className="ph-table" data-testid="monitors-table">
          <thead>
            <tr>
              <th scope="col">monitor</th>
              <th scope="col">trigger</th>
              <th scope="col">cadence</th>
              <th scope="col">last write</th>
              <th scope="col">age</th>
              <th scope="col">legacy</th>
              <th scope="col">coverage note</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ m, unreg }, i) => (
              <tr key={`${m.id}-${i}`} data-monitor={m.id} data-testid="monitor-row">
                <td>
                  <code>{m.id}</code>
                  {m.store_key && m.store_key !== m.id ? <span className="ph-hint"> (store key {m.store_key})</span> : null}
                  {unreg ? <span className="ph-hint"> · in the store, not in the topology model</span> : null}
                </td>
                <td>{triggerCell(m)}</td>
                <td>{cadenceCell(m)}</td>
                <td>{!m.publishes ? "does not publish" : m.last_write_utc ? fmtUtc(m.last_write_utc) : "never written"}</td>
                <td>{m.last_write_utc ? fmtAgeH(m.age_h) : "-"}</td>
                <td>{m.legacy ? "yes - cannot report UNKNOWN" : "no"}</td>
                <td>{m.coverage_note ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="ph-note" data-testid="registry-excluded">
        left off the page:{" "}
        {ex.length > 0
          ? ex.map((x, i) => (
              <span key={x.id}>
                {i > 0 ? "; " : ""}
                <code>{x.id}</code> - {x.why}
              </span>
            ))
          : "none"}
      </p>
      <p className="ph-note" data-testid="store-excluded">
        excluded from the store check - push-triggered, no cadence, so their absence cannot be judged:{" "}
        {data.store.excluded.length > 0 ? data.store.excluded.join(", ") : "none"}
      </p>
    </details>
  );
}
